/**
 * PasswordAuditPanel — read-only password-history inspector.
 *
 * Lives under Admin → Maintenance → Password Audit. Lets the admin
 * search a customer by email and see:
 *   • a sanitized user summary,
 *   • the most recent password_hash_history entries (no hash values),
 *   • a list of safety warnings (duplicates, stale legacy field,
 *     recent password change, disabled/blocked, etc.).
 *
 * STRICTLY READ-ONLY. No buttons to reset, change, or impersonate.
 * The endpoint also sanitises hash values server-side so no raw
 * bcrypt blob ever reaches the network response.
 */
import React, { useState, useCallback } from 'react';
import {
  Search,
  ShieldAlert,
  ShieldCheck,
  Loader2,
  AlertTriangle,
  Info,
  History,
  UserCheck,
  XCircle,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtDt = (iso) => {
  if (!iso) return '—';
  try {
    const d = typeof iso === 'string' ? new Date(iso) : iso;
    if (Number.isNaN(d.getTime())) return String(iso);
    return d.toISOString().replace('T', ' ').replace(/\.\d+Z$/, ' UTC');
  } catch {
    return String(iso);
  }
};

const Pill = ({ tone = 'slate', children, testid }) => {
  const tones = {
    slate:   'bg-slate-100 text-slate-700 border-slate-200',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    amber:   'bg-amber-50 text-amber-700 border-amber-200',
    rose:    'bg-rose-50 text-rose-700 border-rose-200',
    sky:     'bg-sky-50 text-sky-700 border-sky-200',
  };
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-semibold ${tones[tone]}`}
      data-testid={testid}
    >
      {children}
    </span>
  );
};

const KV = ({ label, value, testid }) => (
  <div className="text-[12px]">
    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">
      {label}
    </div>
    <div className="text-slate-800 font-mono break-all" data-testid={testid}>
      {value ?? '—'}
    </div>
  </div>
);

export default function PasswordAuditPanel({ token }) {
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const runSearch = useCallback(async () => {
    const em = (query || '').trim();
    if (!em) {
      toast.error('Enter an email to search');
      return;
    }
    setLoading(true);
    setError(null);
    setData(null);
    try {
      const r = await fetch(
        `${API_URL}/api/admin/maintenance/password-audit?email=${encodeURIComponent(em)}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j?.detail || `HTTP ${r.status}`);
      setData(j);
    } catch (e) {
      setError(e.message || 'Lookup failed');
    } finally {
      setLoading(false);
    }
  }, [query, token]);

  const onKey = (e) => {
    if (e.key === 'Enter') runSearch();
  };

  const user = data?.user;
  const entries = data?.audit_entries || [];
  const warnings = data?.warnings || [];

  return (
    <section
      className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 sm:p-5"
      data-testid="password-audit-panel"
    >
      <div className="flex items-center gap-2 mb-3">
        <div className="w-9 h-9 rounded-lg bg-slate-50 flex items-center justify-center border border-slate-200">
          <ShieldCheck className="w-5 h-5 text-slate-700" />
        </div>
        <div>
          <h3 className="font-semibold text-slate-800" data-testid="password-audit-title">
            Password Audit
          </h3>
          <p className="text-[12px] text-slate-500">
            Read-only inspector for the <code className="text-slate-700">password_hash_history</code> log.
            Hash values are never exposed.
          </p>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-2">
        <input
          type="email"
          autoComplete="off"
          spellCheck={false}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKey}
          placeholder="Search by email"
          className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono"
          data-testid="password-audit-email-input"
        />
        <button
          type="button"
          onClick={runSearch}
          disabled={loading}
          className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 disabled:bg-slate-400"
          data-testid="password-audit-search-btn"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          Search
        </button>
      </div>

      {error && (
        <div
          className="mt-3 flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800"
          data-testid="password-audit-error"
        >
          <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {data && (
        <div className="mt-4 space-y-4">
          {/* ── User summary ───────────────────────────────────── */}
          <div className="border border-slate-200 rounded-lg p-3 bg-slate-50/40" data-testid="password-audit-user-card">
            <div className="flex items-center gap-2 mb-2">
              <UserCheck className="w-4 h-4 text-slate-600" />
              <h4 className="text-[13px] font-bold text-slate-800">User summary</h4>
              {user ? (
                <>
                  {data.duplicate_count > 1 && (
                    <Pill tone="rose" testid="password-audit-duplicate-pill">
                      {data.duplicate_count} duplicates
                    </Pill>
                  )}
                  {user.is_active === false || user.disabled || user.blocked ? (
                    <Pill tone="rose" testid="password-audit-disabled-pill">
                      INACTIVE
                    </Pill>
                  ) : (
                    <Pill tone="emerald" testid="password-audit-active-pill">
                      ACTIVE
                    </Pill>
                  )}
                  <Pill tone="slate" testid="password-audit-role-pill">
                    role: {user.role || '—'}
                  </Pill>
                  <Pill tone={data.has_password_hash ? 'emerald' : 'rose'} testid="password-audit-hash-present-pill">
                    {data.has_password_hash ? 'password_hash present' : 'NO password_hash'}
                  </Pill>
                  {data.has_hashed_password && (
                    <Pill tone="amber" testid="password-audit-legacy-pill">
                      legacy hashed_password present
                    </Pill>
                  )}
                </>
              ) : (
                <Pill tone="rose" testid="password-audit-not-found-pill">USER NOT FOUND</Pill>
              )}
            </div>

            {user ? (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-2">
                <KV label="email"      value={user.email}      testid="password-audit-user-email" />
                <KV label="user id"    value={user.id}         testid="password-audit-user-id" />
                <KV label="role"       value={user.role}       testid="password-audit-user-role" />
                <KV label="is_active"  value={String(user.is_active ?? '—')} testid="password-audit-user-active" />
                <KV label="disabled"   value={String(user.disabled ?? '—')}  testid="password-audit-user-disabled" />
                <KV label="blocked"    value={String(user.blocked ?? '—')}   testid="password-audit-user-blocked" />
                <KV label="created_at" value={fmtDt(user.created_at)} testid="password-audit-user-created" />
                <KV label="updated_at" value={fmtDt(user.updated_at)} testid="password-audit-user-updated" />
                <KV label="last_ip"    value={user.last_ip || '—'} testid="password-audit-user-last-ip" />
                <KV
                  label="last successful login"
                  value={
                    data.last_login
                      ? `${fmtDt(data.last_login.created_at)} · ${data.last_login.ip || '?'}`
                      : '—'
                  }
                  testid="password-audit-user-last-login"
                />
                <KV label="legacy_id"  value={user.legacy_id || '—'} testid="password-audit-user-legacy-id" />
              </div>
            ) : (
              <p className="text-sm text-slate-600" data-testid="password-audit-user-empty">
                No user matches that email.
              </p>
            )}
          </div>

          {/* ── Warnings ───────────────────────────────────────── */}
          {warnings.length > 0 && (
            <div className="space-y-2" data-testid="password-audit-warnings">
              {warnings.map((w, i) => {
                const tone =
                  w.level === 'error' ? 'rose' :
                  w.level === 'warn'  ? 'amber' : 'sky';
                const palette = {
                  rose:  'bg-rose-50 border-rose-200 text-rose-800',
                  amber: 'bg-amber-50 border-amber-200 text-amber-800',
                  sky:   'bg-sky-50 border-sky-200 text-sky-800',
                };
                const Icon =
                  w.level === 'error' ? ShieldAlert :
                  w.level === 'warn'  ? AlertTriangle : Info;
                return (
                  <div
                    key={i}
                    className={`flex items-start gap-2 rounded-lg border px-3 py-2 text-[13px] ${palette[tone]}`}
                    data-testid={`password-audit-warning-${w.code}`}
                  >
                    <Icon className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>{w.message}</span>
                  </div>
                );
              })}
            </div>
          )}

          {/* ── Audit timeline ─────────────────────────────────── */}
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 border-b border-slate-200">
              <History className="w-4 h-4 text-slate-600" />
              <h4 className="text-[13px] font-bold text-slate-800">
                Password hash history
              </h4>
              <span className="text-[11px] text-slate-500">
                ({entries.length} {entries.length === 1 ? 'entry' : 'entries'}, newest first, max 50)
              </span>
            </div>

            {entries.length === 0 ? (
              <p className="text-sm text-slate-600 px-3 py-4" data-testid="password-audit-empty">
                No password audit history found for this user.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-[12px]" data-testid="password-audit-table">
                  <thead className="bg-slate-50/60 text-slate-600">
                    <tr>
                      <th className="text-left px-3 py-2 font-semibold whitespace-nowrap">When (UTC)</th>
                      <th className="text-left px-3 py-2 font-semibold whitespace-nowrap">Source</th>
                      <th className="text-left px-3 py-2 font-semibold whitespace-nowrap">By</th>
                      <th className="text-left px-3 py-2 font-semibold whitespace-nowrap">User</th>
                      <th className="text-left px-3 py-2 font-semibold whitespace-nowrap">Old hash</th>
                      <th className="text-left px-3 py-2 font-semibold whitespace-nowrap">New hash</th>
                      <th className="text-left px-3 py-2 font-semibold whitespace-nowrap">IP</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entries.map((e, i) => (
                      <tr
                        key={i}
                        className="border-t border-slate-100"
                        data-testid={`password-audit-row-${i}`}
                      >
                        <td className="px-3 py-2 font-mono text-slate-800 whitespace-nowrap">
                          {fmtDt(e.created_at)}
                        </td>
                        <td className="px-3 py-2 text-slate-700">{e.source || '—'}</td>
                        <td className="px-3 py-2 text-slate-700">{e.by_email || '—'}</td>
                        <td className="px-3 py-2 font-mono text-slate-600 break-all">
                          <div>{e.user_email || '—'}</div>
                          {e.user_id && (
                            <div className="text-[10px] text-slate-400">{e.user_id}</div>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <Pill tone={e.old_hash_present ? 'slate' : 'amber'}>
                            {e.old_hash_present ? 'present' : 'absent'}
                          </Pill>
                        </td>
                        <td className="px-3 py-2">
                          <Pill tone={e.new_hash_present ? 'emerald' : 'amber'}>
                            {e.new_hash_present ? 'present' : 'absent'}
                          </Pill>
                        </td>
                        <td className="px-3 py-2 font-mono text-slate-600">{e.ip || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
